import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { attendanceService } from "../../api/attendanceService";
import Badge from "../../components/ui/Badge";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import {
  formatDistance,
  getDeviceLocation,
  LocationError,
} from "../../utils/geolocation";
import { formatManilaTime } from "../../utils/manilaTime";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import { DAY_STATUS, formatHoursOrDash } from "../dtr/dtrUtils";
import {
  ATTENDANCE_SUMMARY_KEY,
  MY_ATTENDANCE_SETTINGS_KEY,
  MY_TODAY_KEY,
} from "./sessionUtils";
import { useServerClock } from "./useServerClock";
import { useBranchDistance } from "./useBranchDistance";
import SessionTimeline from "./components/SessionTimeline";
import BetweenCheckIns from "../../../components/ui/BetweenCheckIns";
import OvertimeTag from "../overtime/OvertimeTag";
import LocationHelpModal from "./components/LocationHelpModal";
import ui from "../../components/ui/ui.module.css";
import dashboard from "../dashboard/Dashboard.module.css";
import styles from "./Sessions.module.css";

const MAX_REMARKS = 500;

const formatClock = (date) =>
  date.toLocaleTimeString("en-PH", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Manila",
  });

const formatClockDate = (date) =>
  date.toLocaleDateString("en-PH", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "Asia/Manila",
  });

// Server clock: shows "Syncing…" until the server answered.
const ServerClock = () => {
  const { now, isError } = useServerClock();

  return (
    <div className={styles.clock}>
      <span className={styles.clockTime}>
        {now ? formatClock(now) : isError ? "--:--" : "Syncing…"}
      </span>
      <span className={styles.clockMeta}>
        {now
          ? `${formatClockDate(now)} · Manila time (server)`
          : isError
            ? "Couldn't reach the server clock"
            : "Getting the server time"}
      </span>
    </div>
  );
};

// Opens the recorded point in Google Maps (new tab). Nothing when no
// location was saved for it.
const MapLink = ({ point, label }) =>
  point?.mapUrl ? (
    <a
      href={point.mapUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="small"
    >
      <i className="bi bi-geo-alt me-1"></i>
      {label}
    </a>
  ) : null;

// Branch, radius and the live distance from it.
const BranchInfo = ({ branch, rules, distance }) => {
  if (!branch) return null;
  const isFar =
    distance != null && branch.radius != null && distance > branch.radius;

  return (
    <div className={styles.branchRow}>
      <i className="bi bi-building"></i>
      <span className={styles.branchName}>{branch.name}</span>

      {branch.hasLocation ? (
        <>
          <span>Check-in radius {formatDistance(branch.radius)}</span>
          {distance != null && (
            <span className={isFar ? styles.distanceFar : styles.distanceOk}>
              You are ~{formatDistance(distance)} away
            </span>
          )}
        </>
      ) : (
        <span>No branch location set, distance is not checked</span>
      )}

      {rules?.canCheckInAnywhere && (
        <Badge tone="blue" icon="bi-globe">
          Can check in anywhere
        </Badge>
      )}
    </div>
  );
};

// Today's check-in / check-out. The server decides everything
// (canCheckIn / canCheckOut / lockedReason, the time, the distance rule);
// the device only supplies its location.
const otWindow = (w) =>
  `${formatManilaTime(w.start)}–${formatManilaTime(w.end)}`;

// While checked in: only approved overtime counts, so say whether there is any.
// todayOvertime = { approved: [{ id, start, end, minutes }], pending: [...] }
const TodayOvertime = ({ ot }) => {
  if (!ot) return null;
  const approved = ot.approved ?? [];
  const pending = ot.pending ?? [];
  if (approved.length)
    return (
      <div className={ui.hint}>
        <i className="bi bi-check-circle text-success me-1"></i>
        Approved overtime today: {approved.map(otWindow).join(", ")}
      </div>
    );
  return (
    <div className={cx(ui.alert, ui.alertWarning)}>
      <span>
        <i className="bi bi-clock me-2"></i>
        {pending.length
          ? `Overtime ${pending.map(otWindow).join(", ")} is waiting for approval. Time after your shift counts only once it's approved.`
          : "No approved overtime today: time after your shift won't count."}{" "}
        {!pending.length && <Link to="/me/overtime">File overtime first</Link>}
      </span>
    </div>
  );
};

const CheckInPanel = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const employeeId = getEmployeeId();

  const [remarks, setRemarks] = useState(""); // the check-in / check-out note
  const [noteOpen, setNoteOpen] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [dialog, setDialog] = useState(null); // null | "help"

  const todayQuery = useQuery({
    queryKey: MY_TODAY_KEY,
    queryFn: attendanceService.getMyToday,
    enabled: !!employeeId,
    refetchOnWindowFocus: true,
  });

  const settingsQuery = useQuery({
    queryKey: MY_ATTENDANCE_SETTINGS_KEY,
    queryFn: attendanceService.getMySettings,
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });

  const today = todayQuery.data;
  const rules = today?.rules ?? settingsQuery.data;
  const branchDistance = useBranchDistance(settingsQuery.data?.branch);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: MY_TODAY_KEY });
    queryClient.invalidateQueries({ queryKey: [ATTENDANCE_SUMMARY_KEY] });
  };

  const locate = async (actionLabel) => {
    setIsLocating(true);
    try {
      const location = await getDeviceLocation(actionLabel);
      branchDistance.report(location);
      return location;
    } finally {
      setIsLocating(false);
    }
  };

  const onSuccess = (message) => () => {
    setRemarks("");
    setNoteOpen(false);
    toast.success(message);
  };

  const checkInMutation = useMutation({
    mutationFn: async () => {
      const location = await locate("check in");
      return attendanceService.checkIn({
        employeeId,
        source: "WEB",
        location,
        ...(remarks.trim() && { note: remarks.trim() }),
      });
    },
    onMutate: () => checkOutMutation.reset(),
    onSuccess: onSuccess("Checked in."),
    onSettled: refresh,
  });

  const checkOutMutation = useMutation({
    mutationFn: async () => {
      const location = await locate("check out");
      return attendanceService.checkOut({
        attendanceId: today.attendance.id,
        location,
        ...(remarks.trim() && { note: remarks.trim() }),
      });
    },
    onMutate: () => checkInMutation.reset(),
    onSuccess: onSuccess("Checked out."),
    onSettled: refresh,
  });

  if (!employeeId) {
    return (
      <div className={cx(ui.alert, ui.alertGray)}>
        Your account is not linked to an employee record.
      </div>
    );
  }

  if (todayQuery.isLoading) {
    return (
      <div className={ui.empty}>
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        Loading today…
      </div>
    );
  }

  if (todayQuery.isError) {
    return (
      <div className={cx(ui.alert, ui.alertDanger)}>
        <span>
          {getErrorMessage(
            todayQuery.error,
            "Unable to load today's attendance.",
          )}
        </span>
        <button
          type="button"
          className={cx(ui.button, ui.secondary, ui.buttonSmall)}
          onClick={() => todayQuery.refetch()}
        >
          Retry
        </button>
      </div>
    );
  }

  const sessions = today.sessions ?? [];
  const day = today.attendance;
  const allowMultiple = !!rules?.allowMultipleCheckIns;
  const isSaving = checkInMutation.isPending || checkOutMutation.isPending;
  const error = checkInMutation.error ?? checkOutMutation.error;
  const isLocationError = error instanceof LocationError;
  const status =
    day?.status &&
    (DAY_STATUS[day.status] ?? { label: day.status, tone: "gray" });
  // Single check-in: the day's first check-in and last check-out.
  const firstSession = sessions[0];
  const lastSession = sessions[sessions.length - 1];
  const isNextStop = sessions.length > 0;

  const buttonLabel = (mutation, idle, saving) =>
    mutation.isPending
      ? isLocating
        ? "Getting your location…"
        : saving
      : idle;

  // Notes are mostly for field visits ("Client / notes"): always shown with
  // multiple check-ins, behind "Add a note" otherwise.
  const canAct = !today.lockedReason && (today.canCheckIn || today.canCheckOut);
  const showNotes = canAct && (allowMultiple || noteOpen);
  // The day at a glance: first check-in → last check-out (open session: still in)
  const summary = today.summary;
  const firstIn = summary?.firstCheckIn ?? day?.checkInTime;
  const lastOut = summary ? summary.lastCheckOut : day?.checkOutTime;

  return (
    <div className={styles.panelStack}>
      <ServerClock />

      <BranchInfo
        branch={settingsQuery.data?.branch}
        rules={rules}
        distance={branchDistance.distance}
      />

      {(!allowMultiple || sessions.length > 0) && (
        <div className={dashboard.times}>
          <div className={dashboard.timeBox}>
            <div className={ui.fieldLabel}>
              {allowMultiple ? "First check-in" : "Check in"}
            </div>
            <div className={dashboard.timeValue}>
              {formatManilaTime(firstIn)}
            </div>
            {day?.checkInTime && (
              <MapLink
                point={firstSession?.checkIn}
                label="View check-in location"
              />
            )}
          </div>
          <div className={dashboard.timeBox}>
            <div className={ui.fieldLabel}>
              {allowMultiple ? "Last check-out" : "Check out"}
            </div>
            <div className={dashboard.timeValue}>
              {lastOut
                ? formatManilaTime(lastOut)
                : firstIn && today.canCheckOut
                  ? "Still in"
                  : formatManilaTime(null)}
            </div>
            {day?.checkOutTime && (
              <MapLink
                point={lastSession?.checkOut}
                label="View check-out location"
              />
            )}
          </div>
        </div>
      )}

      {summary && summary.sessionCount > 0 && (
        <div className={ui.hint}>
          {summary.sessionCount} check-in
          {summary.sessionCount === 1 ? "" : "s"} today
          {summary.workedHours > 0 &&
            ` · ${formatHoursOrDash(summary.workedHours)} h worked`}
          {summary.overtimeHours > 0 &&
            ` · ${formatHoursOrDash(summary.overtimeHours)} h approved overtime`}
        </div>
      )}

      {allowMultiple && sessions.length > 0 && (
        <div className={ui.label}>Today&apos;s check-ins</div>
      )}
      {allowMultiple && sessions.length > 1 && (
        <div className={ui.hint}>
          Your hours run from your first check-in to your last check-out, up to
          regular hours unless overtime is approved.
        </div>
      )}
      {allowMultiple && sessions.length > 0 && (
        <SessionTimeline
          sessions={sessions}
          renderExtra={(session) => (
            <div className="d-flex flex-wrap gap-3">
              <MapLink point={session.checkIn} label="View check-in location" />
              {session.checkOutTime && (
                <MapLink
                  point={session.checkOut}
                  label="View check-out location"
                />
              )}
            </div>
          )}
        />
      )}
      {allowMultiple && <BetweenCheckIns gaps={today.gaps} />}

      {/* Single check-in: still show a review flag on today's one session. */}
      {!allowMultiple &&
        sessions
          .filter((s) => s.status === "FOR_REVIEW" || s.status === "REJECTED")
          .map((s) => (
            <SessionTimeline key={s.id ?? s.checkInTime} sessions={[s]} />
          ))}

      {canAct && !showNotes && (
        <button
          type="button"
          className={cx(ui.linkButton, "align-self-start")}
          onClick={() => setNoteOpen(true)}
        >
          <i className="bi bi-chat-left-text me-1"></i>Add a note
        </button>
      )}

      {showNotes && (
        <div>
          <label htmlFor="checkin-notes" className={ui.label}>
            {allowMultiple ? "Client / notes (optional)" : "Note (optional)"}
          </label>
          <input
            id="checkin-notes"
            className={cx(ui.input, styles.notesInput)}
            maxLength={MAX_REMARKS}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder={
              today.canCheckOut
                ? "e.g. Visit done, PO signed"
                : "e.g. ABC Corp, Ortigas"
            }
            disabled={isSaving}
          />
        </div>
      )}

      {today.clientVisit && (
        <div className={cx(ui.alert, ui.alertInfo)}>
          <span>
            <i className="bi bi-briefcase me-1"></i>
            Client visit today:{" "}
            {(today.clientVisit.clients ?? []).map((c) => c.name).join(", ")}
            {today.clientVisit.purpose ? ` · ${today.clientVisit.purpose}` : ""}
            {(today.clientVisit.clients ?? []).some((c) => !c.hasLocation) &&
              ". A client has no saved location, so check-ins there are flagged for review."}
          </span>
        </div>
      )}

      {today.lockedReason ? (
        <div className={cx(ui.alert, ui.alertGray)}>
          <span>
            <i className="bi bi-lock me-2"></i>
            {today.lockedReason}
          </span>
        </div>
      ) : today.canCheckOut && day?.id ? (
        <button
          type="button"
          className={cx(ui.button, dashboard.bigButton, dashboard.checkOut)}
          onClick={() => checkOutMutation.mutate()}
          disabled={isSaving}
        >
          <i className="bi bi-box-arrow-right"></i>
          {buttonLabel(checkOutMutation, "Check out", "Checking out…")}
        </button>
      ) : today.canCheckIn ? (
        <button
          type="button"
          className={cx(ui.button, dashboard.bigButton, dashboard.checkIn)}
          onClick={() => checkInMutation.mutate()}
          disabled={isSaving}
        >
          <i className="bi bi-box-arrow-in-right"></i>
          {buttonLabel(
            checkInMutation,
            isNextStop ? "Check in again (next stop)" : "Check in",
            "Checking in…",
          )}
        </button>
      ) : (
        sessions.length > 0 && (
          <div className={cx(ui.alert, ui.alertSuccess)}>
            <span>
              <i className="bi bi-check-circle me-2"></i>
              Done for today.
            </span>
          </div>
        )
      )}

      {today.canCheckOut && <TodayOvertime ot={today.todayOvertime} />}

      {error && (
        <div
          className={cx(
            ui.alert,
            isLocationError ? ui.alertWarning : ui.alertDanger,
          )}
          role="alert"
        >
          <span>
            <i
              className={`bi ${isLocationError ? "bi-geo-alt" : "bi-exclamation-circle"} me-2`}
            ></i>
            {isLocationError
              ? error.message
              : getErrorMessage(
                  error,
                  "Something went wrong. Please try again.",
                )}
            {/* Outside the branch radius: say who can change it */}
            {!isLocationError &&
              /only allowed within|km from|m from/i.test(
                getErrorMessage(error, ""),
              ) && (
                <span className="d-block mt-1">
                  Ask HR to allow &quot;Check in anywhere&quot; for you or your
                  role.
                </span>
              )}
          </span>
          {isLocationError && (
            <button
              type="button"
              className={ui.linkButton}
              onClick={() => setDialog("help")}
            >
              How to allow location
            </button>
          )}
        </div>
      )}

      <div className={dashboard.todayFooter}>
        <span className={ui.badgeGroup}>
          {status && <Badge tone={status.tone}>{status.label}</Badge>}
          {day?.isLate && day.status !== "LATE" && (
            <Badge tone="amber">Late</Badge>
          )}
          <OvertimeTag day={day} />
          {allowMultiple && (
            <Badge tone="gray" icon="bi-signpost-split">
              Multiple check-ins
              {rules.multipleCheckInsFrom === "ROLE" ? " (role)" : ""}
            </Badge>
          )}
        </span>
        <span className="d-inline-flex flex-wrap gap-3">
          {day?.workHours > 0 && (
            <span>Worked {formatHoursOrDash(day.workHours)}</span>
          )}
          {!error && (
            <button
              type="button"
              className={ui.linkButton}
              onClick={() => setDialog("help")}
            >
              Location help
            </button>
          )}
        </span>
      </div>

      {dialog === "help" && (
        <LocationHelpModal onClose={() => setDialog(null)} />
      )}
    </div>
  );
};

export default CheckInPanel;
